import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Card, Descriptions, Space, Tag, Typography } from 'antd'
import { FileTextOutlined } from '@ant-design/icons'
import {
  errorNotification,
  postRequest,
  trackingDocumentLinkUrl
} from '../../config'
import { errorSentence, formatDateTime } from './utils'

// The document a WhatsApp shipment came out of, and what we read off it (spec:
// whatsapp-shipment-tracking Req 1.1).
//
// Two things it exists for. When a reading has gone wrong, the page is the only thing that can
// settle what the bill of lading actually said — so the team can open it. And when a customer
// rings about a shipment, the details we read are what they are looking at.
//
// A link is minted when the button is pressed and lives minutes. One stored in the payload would
// outlive the page it was built for, and the file is somebody's shipping paperwork: it names the
// shipper, the consignee and the notify party with their addresses.
const BillOfLadingPanel = ({ bill }) => {
  const [opening, setOpening] = useState(null)
  const row = bill || {}
  const documents = row.documents || []
  const reading = row.reading || {}

  const open = async document => {
    const response = await postRequest(
      trackingDocumentLinkUrl(document.id),
      {},
      'document link',
      () => setOpening(document.id)
    )
    setOpening(null)
    if (!response.success) {
      errorNotification(errorSentence(response, 'That document could not be opened.'))
      return
    }
    const url = response.data && response.data.url
    // `noopener,noreferrer` with the comma: passing `"noopener"` as the NAME returns null and
    // navigates this tab away instead (the bug the w-site audit found).
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }

  const containers = reading.containers || []
  const vins = reading.vins || []

  return (
    <Card
      title="Bill of lading"
      extra={
        row.source ? (
          <Tag>{row.source === 'text_layer' ? 'Read from the PDF text' : `Read by ${row.source}`}</Tag>
        ) : null
      }
    >
      <Space wrap style={{ marginBottom: 12 }}>
        {documents.length === 0 ? (
          <span style={{ opacity: 0.65 }}>No document was stored.</span>
        ) : null}
        {documents.map(document =>
          document.deletedAt ? (
            // The row outlives the file on purpose: "this document existed and was deleted on
            // this date" is the only honest record of a retention policy.
            <Tag key={document.id}>
              {`${document.filename || 'Document'} · deleted ${formatDateTime(
                document.deletedAt
              )}`}
            </Tag>
          ) : (
            <Button
              key={document.id}
              icon={<FileTextOutlined />}
              loading={opening === document.id}
              onClick={() => open(document)}
            >
              {document.filename || `Document (${document.format || 'file'})`}
            </Button>
          )
        )}
      </Space>

      <Descriptions size="small" bordered column={2}>
        <Descriptions.Item label="Shipping line">
          {reading.lineLabel || reading.lineName || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Document">
          {row.documentKind || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="B/L number">
          {reading.blNumber ? (
            <Typography.Text copyable>{reading.blNumber}</Typography.Text>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Booking">
          {reading.bookingNumber || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Vessel">
          {[reading.vessel, reading.voyage].filter(Boolean).join(' ') || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Route">
          {[reading.pol, reading.pod].filter(Boolean).join(' → ') || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Containers" span={2}>
          {containers.length ? containers.join(', ') : '—'}
        </Descriptions.Item>
        {/* RoRo. The whole VIN, unlike the customer's own copy: the team phones shipping lines
            about these, and a masked one would be useless to them. */}
        {vins.length ? (
          <Descriptions.Item label="VINs" span={2}>
            {vins.join(', ')}
          </Descriptions.Item>
        ) : null}
        {(row.missing || []).length ? (
          <Descriptions.Item label="Could not read" span={2}>
            {(row.missing || []).join(', ')}
          </Descriptions.Item>
        ) : null}
        <Descriptions.Item label="Read at" span={2}>
          {formatDateTime(row.readAt)}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  )
}

BillOfLadingPanel.propTypes = {
  bill: PropTypes.object
}

export default BillOfLadingPanel
